import * as React from "react";
import { SvgSprite } from "../SvgSprite";

import classNames from "classnames";

interface State {
  isFocused: boolean;
  activeIdx: number;
}

export interface DateRangePickerSelectProps<T> {
  id: string;
  label: string;
  selected: T;
  dataSource: T[];
  renderOption: (option: T, idx: number) => React.ReactNode;
  onSelect: (option: T, idx: number) => void;
  isActive: (option: T, idx: number) => boolean;
  innerRef?: React.MutableRefObject<{
    ul: HTMLUListElement;
    button: HTMLButtonElement;
  }>;
  "aria-labelledby": string;
}

type ListItemProps = React.DetailedHTMLProps<
  React.LiHTMLAttributes<HTMLLIElement>,
  HTMLLIElement
>;

interface ListOptionProps extends ListItemProps {
  active: boolean;
}

const ListOption: React.FC<ListOptionProps> = ({
  active,
  children,
  ...props
}) => {
  const liRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (!active) {
      return;
    }

    if (liRef.current) {
      const parentNode = liRef.current.parentNode as HTMLUListElement;
      const currentNode = liRef.current;

      /* - delta */
      if (currentNode.offsetTop < parentNode.scrollTop) {
        // scrollIntoView with top aligned
        currentNode.scrollIntoView({ block: "nearest" });
        // parentNode.scrollTop = currentNode.offsetTop;
      }

      /* + delta */
      if (
        currentNode.offsetTop + currentNode.clientHeight >
        parentNode.scrollTop + parentNode.clientHeight
      ) {
        // scrollIntoView with bottom aligned
        currentNode.scrollIntoView({ block: "nearest" });
      }
    }
  }, [active]);

  return (
    <li ref={liRef} {...props}>
      {children}
    </li>
  );
};

export const DateRangePickerSelect = <T extends unknown>({
  id,
  label,
  selected,
  dataSource,
  renderOption,
  onSelect,
  isActive,
  innerRef,
  "aria-labelledby": labeledBy,
  ...props
}: DateRangePickerSelectProps<T>): JSX.Element => {
  const ulRef = React.useRef<HTMLUListElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useImperativeHandle(innerRef, () => ({
    ul: ulRef.current,
    button: buttonRef.current,
  }));

  const [state, setState] = React.useState<State>(() => {
    return {
      isFocused: false,
      activeIdx: dataSource.findIndex(isActive),
    };
  });

  const handleBlur = (e: React.FocusEvent<HTMLUListElement>): void => {
    e.stopPropagation();

    if (e.currentTarget === e.target) {
      console.log("blur (self)");
      setState((prev) => ({ ...prev, isFocused: false }));
    }
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      console.log("focusleave");
      setState((prev) => ({ ...prev, isFocused: false }));
    }
  };

  const handleUlKeyDown = (e: React.KeyboardEvent<HTMLUListElement>): void => {
    switch (e.key) {
      case "Escape": {
        setState((prev) => ({
          ...prev,
          isFocused: false,
        }));
        buttonRef.current.focus();
        break;
      }

      case "ArrowUp": {
        e.preventDefault();
        setState((prev) => {
          let nextActiveIdx = prev.activeIdx - 1;

          if (nextActiveIdx < 0) {
            nextActiveIdx = dataSource.length - 1;
          }

          return {
            ...prev,
            activeIdx: nextActiveIdx,
          };
        });
        break;
      }

      case "ArrowDown": {
        e.preventDefault();
        setState((prev) => {
          let nextActiveIdx = prev.activeIdx + 1;

          if (nextActiveIdx > dataSource.length - 1) {
            nextActiveIdx = 0;
          }

          return {
            ...prev,
            activeIdx: nextActiveIdx,
          };
        });
        break;
      }

      case "Home": {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          activeIdx: 0,
        }));
        break;
      }

      case "End": {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          activeIdx: dataSource.length - 1,
        }));
        break;
      }

      case "Enter": {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          isFocused: false,
        }));
        buttonRef.current.focus();
        break;
      }
    }
  };

  const onSelectRef = React.useRef(onSelect);

  React.useEffect(() => {
    onSelectRef.current = onSelect;
  });

  React.useEffect(() => {
    if (onSelectRef.current && dataSource) {
      onSelectRef.current(dataSource[state.activeIdx], state.activeIdx);
    }
  }, [dataSource, state.activeIdx]);

  React.useEffect(() => {
    if (state.isFocused) {
      ulRef.current.focus();
    }
  }, [state.isFocused]);

  const btnId = `${id}__toggle-btn`;

  return (
    <div {...props}>
      <button
        id={`${id}__toggle-btn`}
        ref={buttonRef}
        type="button"
        css={`
          &:focus {
            position: relative;
            z-index: 1;
          }
        `}
        className="form-control border-0"
        aria-haspopup="listbox"
        aria-expanded={state.isFocused}
        aria-labelledby={`${btnId} ${labeledBy}`}
        onClick={(e) => {
          e.preventDefault();
          setState((prev) => ({
            ...prev,
            isFocused: true,
          }));

          ulRef.current.focus();
        }}
        onKeyDown={(e) => {
          const { key } = e;

          switch (key) {
            case "ArrowUp": {
              e.preventDefault();
              setState((prev) => {
                let nextActiveIdx = prev.activeIdx - 1;

                if (nextActiveIdx < 0) {
                  nextActiveIdx = dataSource.length - 1;
                }

                return {
                  ...prev,
                  isFocused: true,
                  activeIdx: nextActiveIdx,
                };
              });
              break;
            }

            case "ArrowDown": {
              e.preventDefault();
              setState((prev) => {
                let nextActiveIdx = prev.activeIdx + 1;

                if (nextActiveIdx > dataSource.length - 1) {
                  nextActiveIdx = 0;
                }

                return {
                  ...prev,
                  isFocused: true,
                  activeIdx: nextActiveIdx,
                };
              });
              break;
            }
          }
        }}
      >
        <strong>{label}</strong>{" "}
        <SvgSprite
          id="icons:chev-down"
          width={14}
          height={14}
          className="ml-2"
          css={`
            transition: 0.3s ${(p) => p.theme.timingFnEaseOut};
          `}
          style={{
            transform: state.isFocused
              ? "rotate3d(1, 0, 0, 180deg)"
              : "rotate3d(1, 0, 0, 0deg)",
          }}
        />
      </button>
      <ul
        ref={ulRef}
        tabIndex={0}
        role="listbox"
        className="dropdown-menu"
        style={{
          display: state.isFocused ? "block" : "none",
          visibility: state.isFocused ? "visible" : "hidden",
        }}
        css={`
          position: absolute;
          top: 100%;
          background: #fff;
          overflow: auto;
          max-height: 14rem;
        `}
        aria-labelledby={labeledBy}
        aria-activedescendant={`${id}__option__${state.activeIdx}`}
        onBlur={handleBlur}
        onKeyDown={handleUlKeyDown}
      >
        {dataSource.map((n, idx) => {
          return (
            <ListOption
              role="option"
              id={`${id}__option__${idx}`}
              key={idx}
              css={`
                cursor: pointer;
              `}
              className={classNames("dropdown-item", {
                active: idx === state.activeIdx,
              })}
              onClick={(e) => {
                setState((prev) => ({
                  ...prev,
                  isFocused: false,
                  activeIdx: idx,
                }));
                onSelect(n, idx);
              }}
              aria-selected={idx === state.activeIdx}
              active={idx === state.activeIdx}
            >
              {renderOption(n, idx)}
            </ListOption>
          );
        })}
      </ul>
    </div>
  );
};
